import { apiFetch } from '@carouselgg/auth/client'

/**
 * The signed-in user's own fixtures, across every organization.
 *
 * This is resolved from the caller's ROSTER SPOTS, not from their orgs. That
 * distinction is the reason the endpoint exists: a club player competes in a
 * league org they are not a member of, so fanning out over the user's own
 * organizations from the client misses most of a typical player's schedule.
 */

export interface MatchTeam {
  id: string
  match_id: string
  team_id: string
  designation: string
  score: number | null
  team_name: string
  team_logo: string
}

/** Who is hosting. Present on /matches/mine, absent on org-scoped listings. */
export interface MatchOrganization {
  id: string
  name: string
  slug: string
  type: string
  logo_url?: string
  primary_color?: string
}

export interface MyMatch {
  id: string
  status: 'scheduled' | 'live' | 'completed' | 'cancelled' | 'forfeit'
  best_of: number
  /**
   * When `time_tbd` is true this is only a PLACEHOLDER that keeps rounds in
   * order — never display it or reason about the clock with it. Use
   * `isTimeTbd()` rather than reading the flag directly.
   */
  starts_at: string
  /**
   * The start time is not known yet (a tournament round waiting on the one
   * before it). An admin sets the real time later and this flips to false.
   * Optional so older payloads and fixtures read as "time known".
   */
  time_tbd?: boolean
  ends_at: string | null
  game_name: string
  game_slug: string
  game_logo_url: string
  division_name: string | null
  title: string
  organization_id: string
  organization?: MatchOrganization
  teams: MatchTeam[]
  stream_options: { url: string; label: string }[]
}

/**
 * True when the match has no known start time. "We shouldn't show times unless
 * we know for sure" — every caller that formats or compares `starts_at` checks
 * this first.
 */
export function isTimeTbd(match: Pick<MyMatch, 'time_tbd'>): boolean {
  return match.time_tbd === true
}

export interface MyMatchesParams {
  /** Omit for the default "my schedule" view, which excludes cancelled and forfeited. */
  status?: MyMatch['status']
  /** Only matches starting from now onward. */
  upcoming?: boolean
  limit?: number
}

export async function listMyMatches(params: MyMatchesParams = {}): Promise<MyMatch[]> {
  const qs = new URLSearchParams()
  if (params.status) qs.set('status', params.status)
  if (params.upcoming) qs.set('upcoming', 'true')
  if (params.limit) qs.set('limit', String(params.limit))
  const query = qs.toString()
  return apiFetch<MyMatch[]>(`/matches/mine${query ? `?${query}` : ''}`)
}
